import { isAxiosError } from "axios";
import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { getExperienceFeFromDto } from "@src/api/formatters/from-dto/experiences";
import { DashboardPageName } from "@src/api/types/dashboards";
import { mpShareLinkViewed } from "@src/utils/mixpanel";

import { ShareErrorState } from "./components/ShareErrorState";
import { ShareLoadingSkeleton } from "./components/ShareLoadingSkeleton";
import { ShareTab } from "./context/ShareAnalyticsContext";
import { useHydrateReduxFromShare } from "./hooks/useHydrateReduxFromShare";
import { useShareAnalytics } from "./hooks/useShareAnalytics";
import { useShareAnalyticsProducts } from "./hooks/useShareAnalyticsProducts";
import { useShareExperience } from "./hooks/useShareExperience";
import { useShareToken } from "./hooks/useShareToken";
import { generateShareGraphConfigs } from "./utils/generateShareGraphConfigs";
import { getAvailablePages } from "./utils/getAvailablePages";
import { ShareBody } from "./ShareBody";

export const ShareRoute = () => {
  const { experienceId } = useParams<{ experienceId: string }>();
  const { token, isValid, error: tokenError } = useShareToken();

  // Get page name and tab from query params
  const [searchParams] = useSearchParams();
  const pageName =
    (searchParams.get("page") as DashboardPageName) || "overview";
  const tabParam = searchParams.get("tab");
  const activeTab: ShareTab =
    tabParam === "results" || tabParam === "configure-analytics"
      ? tabParam
      : "results";

  const {
    data: experience,
    isLoading: isLoadingExperience,
    error: experienceError,
  } = useShareExperience(experienceId, token);

  // Transform DTO to FE type early - computed once and reused everywhere
  const experienceFe = useMemo(
    () =>
      experience?.experience
        ? getExperienceFeFromDto(experience.experience)
        : undefined,
    [experience?.experience]
  );

  // Generate graph configs from experience metrics based on page name
  const graphConfigs = useMemo(
    () => generateShareGraphConfigs(experience, pageName),
    [experience, pageName]
  );

  // Fetch analytics data for all graph configs
  const analyticsData = useShareAnalytics(
    experienceId,
    experienceFe,
    graphConfigs
  );

  // Hydrate Redux store for components that use useSelector (e.g., MetricDetailsWrapper)
  // Also sets compare groups after analytics data loads
  useHydrateReduxFromShare(
    experience,
    experienceFe,
    analyticsData,
    graphConfigs
  );

  // Fetch and hydrate analytics products for Configure Analytics tab
  useShareAnalyticsProducts(experienceId);

  // Determine available pages for navigation
  const availablePages = useMemo(
    () =>
      experience?.experience ? getAvailablePages(experience.experience) : [],
    [experience?.experience]
  );

  // Track share link view when experience loads successfully
  useEffect(() => {
    if (experienceId && experienceFe) {
      mpShareLinkViewed(experienceId);
    }
  }, [experienceId, experienceFe]);

  // Validate token first
  if (!isValid || tokenError) {
    return <ShareErrorState errorType="no-token" />;
  }

  // Handle experience loading/error states
  if (experienceError) {
    const status = isAxiosError(experienceError)
      ? experienceError.response?.status
      : undefined;
    if (status === 404) {
      return <ShareErrorState errorType="not-found" />;
    }
    if (status === 401 || status === 403) {
      return <ShareErrorState errorType="invalid-token" />;
    }
    return <ShareErrorState errorType="network-error" />;
  }

  if (isLoadingExperience) {
    return <ShareLoadingSkeleton />;
  }

  if (!experience?.experience || !experienceFe) {
    return <ShareErrorState errorType="not-found" />;
  }

  const analyticsError = analyticsData.some((q) => q.isError);
  if (analyticsError) {
    return <ShareErrorState errorType="network-error" />;
  }

  // Data is loaded and validated - render the body
  return (
    <ShareBody
      experience={experience}
      experienceFe={experienceFe}
      analyticsData={analyticsData}
      graphConfigs={graphConfigs}
      pageName={pageName}
      activeTab={activeTab}
      availablePages={availablePages}
    />
  );
};
